/**
 * Active Strategies Dashboard + Execution History (spec Part 21/22) —
 * clicking a strategy row opens `StrategyDetailDialog`
 * (Details -> Legs -> Orders -> P&L -> Greeks -> Risk -> Modify -> Exit),
 * exactly matching Part 21's own drill-down diagram in one click.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import positionService from "@/services/positionService";
import type { ExecutionHistoryRead, StrategyPositionRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { BROKER_DISPLAY_LABELS, fmtCurrency } from "@/pages/strategy-adjustment/constants";
import { STRATEGY_STATUS_COLORS, STRATEGY_STATUS_LABELS } from "@/pages/execution/constants";
import { STRATEGY_POSITION_STATUS_COLORS } from "./constants";
import { exportToCsv } from "@/utils/csv";
import StrategyDetailDialog from "./StrategyDetailDialog";

/** Same date+time convention as OrdersPage.tsx's own `formatLastUpdated` --
 * kept as its own small local copy rather than a shared import, matching
 * this app's existing per-page formatter convention (see StrategiesPage.tsx's
 * own `formatDate`). */
function formatEntryTime(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  const datePart = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const timePart = d.toLocaleTimeString();
  return `${datePart} ${timePart}`;
}

/** Realized P&L as a percentage of capital committed at entry. Both sides
 * of `entry_value` (a manual BUY entry is negative -- a debit; a SHORT/
 * credit-spread leg's entry is positive -- a credit received; see
 * `PositionService._compute_signed_value` and `AlgoTradingView.
 * build_execution_history_reads` for the sign convention) are divided
 * using `entry_value`'s absolute value, so this always reads as "% of
 * capital/margin committed", never flips sign purely because the entry
 * happened to be a credit rather than a debit. Returns `null` (rendered as
 * "—") whenever either figure isn't available yet, or entry_value is
 * exactly 0 -- never a misleading divide-by-zero or a false 0.00%. */
function computeProfitPct(realizedPnl?: number | null, entryValue?: number | null): number | null {
  if (realizedPnl === null || realizedPnl === undefined) return null;
  if (entryValue === null || entryValue === undefined || entryValue === 0) return null;
  return (realizedPnl / Math.abs(entryValue)) * 100;
}

function formatProfitPct(pct: number | null): string {
  if (pct === null || Number.isNaN(pct)) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

type HistorySortColumn = "entry_time" | "strategy_key" | "source" | "broker" | "profit_pct";
type SortDirection = "asc" | "desc";

/** `null`/`undefined` always sorts last, regardless of `dir` -- "no data
 * yet" (a FAILED order with no timestamp, an open position with no
 * %Profit) should sink to the bottom whichever direction is active, never
 * jump to the top just because `dir` flipped. */
function compareSortValues(a: number | string | null | undefined, b: number | string | null | undefined, dir: 1 | -1): number {
  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === "string" && typeof b === "string") return a.localeCompare(b) * dir;
  return ((a as number) - (b as number)) * dir;
}

/** Maps the already-sorted/filtered history rows on screen into the plain
 * `Record<string, unknown>[]` shape `exportToCsv` (`@/utils/csv`, this
 * codebase's one shared CSV-export helper -- see that module's own
 * docstring) expects, one key per column, in display order. Currency/
 * percentage fields are exported as plain numbers (no ₹, no thousands
 * separators) since embedding those in a CSV cell fights spreadsheet
 * software rather than helping it. Built from the array already on
 * screen, not a fresh server call, so the file always matches what the
 * user is currently looking at (same sort order, same rows). */
function buildExecutionHistoryCsvRows(
  rows: { row: ExecutionHistoryRead; profitPct: number | null }[]
): Record<string, unknown>[] {
  return rows.map(({ row, profitPct }) => ({
    "Entry Time": row.entry_time ? new Date(row.entry_time).toLocaleString() : "",
    "Exit Time": row.exit_time ? new Date(row.exit_time).toLocaleString() : "",
    Strategy: row.strategy_key,
    Source: row.source === "ALGO" ? "Algo" : "Manual",
    Broker: BROKER_DISPLAY_LABELS[row.broker],
    "Entry Value": row.entry_value ?? "",
    "Exit Value": row.exit_value ?? "",
    "Realized P&L": row.realized_pnl ?? "",
    "%Profit": profitPct !== null ? profitPct.toFixed(2) : "",
    Status: STRATEGY_STATUS_LABELS[row.status],
    Legs: row.number_of_legs,
  }));
}

export function PositionsPage() {
  const [tab, setTab] = useState<"active" | "history">("active");
  const [strategies, setStrategies] = useState<StrategyPositionRead[]>([]);
  const [history, setHistory] = useState<ExecutionHistoryRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [historySortColumn, setHistorySortColumn] = useState<HistorySortColumn | null>(null);
  const [historySortDirection, setHistorySortDirection] = useState<SortDirection>("asc");

  const handleHistorySort = useCallback(
    (column: HistorySortColumn) => {
      if (historySortColumn === column) {
        setHistorySortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
      } else {
        setHistorySortColumn(column);
        setHistorySortDirection("asc");
      }
    },
    [historySortColumn]
  );

  // Precompute %Profit once per row (reused by both the table cell and the
  // CSV export, and by the sort comparator below) rather than recomputing
  // it three separate times for the same row.
  const historyRows = useMemo(
    () => history.map((row) => ({ row, profitPct: computeProfitPct(row.realized_pnl, row.entry_value) })),
    [history]
  );

  const sortedHistoryRows = useMemo(() => {
    if (!historySortColumn) return historyRows;
    const dir: 1 | -1 = historySortDirection === "asc" ? 1 : -1;
    // Stable sort: `Array.prototype.sort` is spec-guaranteed stable in
    // every modern JS engine, so equal keys keep their original (newest-
    // first, per the service's own ordering) relative order rather than
    // needing an explicit tie-break index.
    return [...historyRows].sort(({ row: a, profitPct: aPct }, { row: b, profitPct: bPct }) => {
      switch (historySortColumn) {
        case "entry_time":
          return compareSortValues(
            a.entry_time ? new Date(a.entry_time).getTime() : null,
            b.entry_time ? new Date(b.entry_time).getTime() : null,
            dir
          );
        case "strategy_key":
          return compareSortValues(a.strategy_key, b.strategy_key, dir);
        case "source":
          return compareSortValues(a.source, b.source, dir);
        case "broker":
          return compareSortValues(BROKER_DISPLAY_LABELS[a.broker], BROKER_DISPLAY_LABELS[b.broker], dir);
        case "profit_pct":
          return compareSortValues(aPct, bPct, dir);
        default:
          return 0;
      }
    });
  }, [historyRows, historySortColumn, historySortDirection]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [active, historyResult] = await Promise.all([
        positionService.listActiveStrategies(),
        positionService.listExecutionHistory(),
      ]);
      setStrategies(active.strategies);
      setHistory(historyResult.executions);
      setLastSynced(new Date());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <AccountTreeIcon color="primary" />
       
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        
        <Stack direction="row" spacing={2} alignItems="center">
          {lastSynced && (
            <Typography variant="caption" color="text.secondary">
              Last synchronized: {lastSynced.toLocaleTimeString()}
            </Typography>
          )}
          {tab === "history" && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() =>
                exportToCsv(
                  `execution-history-${new Date().toISOString().slice(0, 10)}.csv`,
                  buildExecutionHistoryCsvRows(sortedHistoryRows)
                )
              }
              disabled={loading || history.length === 0}
            >
              Download CSV
            </Button>
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_e, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="active" label="Active Strategies" />
        <Tab value="history" label="Execution History" />
      </Tabs>

      {loading && strategies.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : tab === "active" ? (
        strategies.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active strategies — every executed strategy is either still being placed or already closed.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Strategy</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell>Underlying</TableCell>
                <TableCell align="right">Current P&L</TableCell>
                <TableCell align="right">Remaining Risk</TableCell>
                <TableCell>Execution Status</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date/Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.map((strategy) => {
                // Algo-sourced rows key `execution_id` off `AlgoPosition.id`,
                // not a real `Execution` (see app.services.execution.
                // algo_trading_view's module docstring) -- the manual-only
                // StrategyDetailDialog (GET /strategies/{execution_id}/
                // position) would 404 on it, so algo rows are view-only here
                // (see the Algo Execution Monitor for their own drill-down).
                const isAlgo = strategy.source === "ALGO";
                return (
                  <TableRow
                    key={strategy.execution_id}
                    hover={!isAlgo}
                    sx={{ cursor: isAlgo ? "default" : "pointer" }}
                    onClick={() => !isAlgo && setSelectedExecutionId(strategy.execution_id)}
                  >
                    <TableCell>
                      <Chip
                        size="small"
                        label={isAlgo ? "Algo" : "Manual"}
                        color={isAlgo ? "info" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{strategy.strategy_key}</TableCell>
                    <TableCell>{BROKER_DISPLAY_LABELS[strategy.broker]}</TableCell>
                    <TableCell>{strategy.underlying}</TableCell>
                    <TableCell align="right" sx={{ color: (strategy.current_pnl ?? 0) >= 0 ? "success.main" : "error.main" }}>
                      {fmtCurrency(strategy.current_pnl)}
                    </TableCell>
                    <TableCell align="right">{fmtCurrency(strategy.remaining_risk)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={STRATEGY_STATUS_LABELS[strategy.strategy_execution_status]} color={STRATEGY_STATUS_COLORS[strategy.strategy_execution_status]} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={strategy.status.replace(/_/g, " ")} color={STRATEGY_POSITION_STATUS_COLORS[strategy.status] ?? "default"} />
                    </TableCell>
                    <TableCell>{formatEntryTime(strategy.entry_time)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )
      ) : history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No execution history yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={historySortColumn === "entry_time"}
                  direction={historySortColumn === "entry_time" ? historySortDirection : "asc"}
                  onClick={() => handleHistorySort("entry_time")}
                >
                  Entry Time
                </TableSortLabel>
              </TableCell>
              <TableCell>Exit Time</TableCell>
              <TableCell>
                <TableSortLabel
                  active={historySortColumn === "strategy_key"}
                  direction={historySortColumn === "strategy_key" ? historySortDirection : "asc"}
                  onClick={() => handleHistorySort("strategy_key")}
                >
                  Strategy
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={historySortColumn === "source"}
                  direction={historySortColumn === "source" ? historySortDirection : "asc"}
                  onClick={() => handleHistorySort("source")}
                >
                  Source
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={historySortColumn === "broker"}
                  direction={historySortColumn === "broker" ? historySortDirection : "asc"}
                  onClick={() => handleHistorySort("broker")}
                >
                  Broker
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Entry Value</TableCell>
              <TableCell align="right">Exit Value</TableCell>
              <TableCell align="right">Realized P&L</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={historySortColumn === "profit_pct"}
                  direction={historySortColumn === "profit_pct" ? historySortDirection : "asc"}
                  onClick={() => handleHistorySort("profit_pct")}
                >
                  %Profit
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Legs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedHistoryRows.map(({ row, profitPct }) => {
              // Algo rows key `execution_id` off `AlgoPosition.id`, not a
              // real `Execution` -- same reasoning, and same click-guard,
              // as the Active Strategies table above (clicking through
              // would 404 against the manual-only StrategyDetailDialog).
              const isAlgo = row.source === "ALGO";
              const isClickable = !isAlgo && row.execution_purpose === "ENTRY";
              return (
                <TableRow
                  key={row.execution_id}
                  hover={isClickable}
                  sx={{ cursor: isClickable ? "pointer" : "default" }}
                  onClick={() => isClickable && setSelectedExecutionId(row.execution_id)}
                >
                  <TableCell>{row.entry_time ? new Date(row.entry_time).toLocaleString() : "—"}</TableCell>
                  <TableCell>{row.exit_time ? new Date(row.exit_time).toLocaleString() : "—"}</TableCell>
                  <TableCell>{row.strategy_key}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={isAlgo ? "Algo" : "Manual"}
                      color={isAlgo ? "info" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{BROKER_DISPLAY_LABELS[row.broker]}</TableCell>
                  <TableCell align="right">{fmtCurrency(row.entry_value)}</TableCell>
                  <TableCell align="right">{fmtCurrency(row.exit_value)}</TableCell>
                  <TableCell align="right">{fmtCurrency(row.realized_pnl)}</TableCell>
                  <TableCell
                    align="right"
                    sx={profitPct !== null ? { color: profitPct >= 0 ? "success.main" : "error.main" } : undefined}
                  >
                    {formatProfitPct(profitPct)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={STRATEGY_STATUS_LABELS[row.status]} color={STRATEGY_STATUS_COLORS[row.status]} />
                  </TableCell>
                  <TableCell align="right">{row.number_of_legs}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <StrategyDetailDialog
        executionId={selectedExecutionId}
        open={Boolean(selectedExecutionId)}
        onClose={() => {
          setSelectedExecutionId(null);
          load();
        }}
      />
    </Container>
  );
}

export default PositionsPage;
